import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators, FormArray, AbstractControl, FormControlName } from '@angular/forms';
import { nanoid } from 'nanoid';
import { ProjectService } from "../../../services/project.service";
import { HttpClient } from '@angular/common/http'
import { ProjectManagerComponent } from '../project-manager.component';
import { FileUploadService } from "../../../services/file-upload.service";
import { NotifierService } from 'angular-notifier';
import { Observable, Subject } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { SessionsFormComponent } from '../../forms/sessions-form/sessions-form.component';
import { DocumentationFormComponent } from '../../forms/documentation-form/documentation-form.component';
import { environment } from 'src/environments/environment';
import { SystemService } from 'src/app/services/system.service';

@Component({
  selector: 'app-project-dialog',
  templateUrl: './project-dialog.component.html',
  styleUrls: ['./project-dialog.component.scss']
})
export class ProjectDialogComponent implements OnInit, OnDestroy {
  @ViewChild(SessionsFormComponent, { static: false }) public emudbFormComponent: SessionsFormComponent;
  @ViewChild(DocumentationFormComponent, { static: false }) public docsFormComponent: DocumentationFormComponent;

  EMUDB_INTEGRATION = environment.EMUDB_INTEGRATION;

  @Input() projectManager: ProjectManagerComponent;

  submitBtnLabel:string = "Create project";
  isLoading:boolean = false;
  submitBtnEnabled:boolean = false;
  pendingUpload:boolean = false;
  showLoadingIndicator:boolean = false;
  submitBtnEnabledLockout:boolean = false;
  formValidationInterval:any;
  fileUploadsComlete:boolean = true;
  validateWaitInterval:any = null;
  setupEmuDbFormChangeListener:any = null;
  emuDbIntegrationEnabled:boolean = true;
  docFiles:FormArray;
  project:any = null;
  emuDb:any = null;
  dialogTitle:string = "Create new project";
  emuDbLoadedSubject:Subject<boolean> = new Subject<boolean>();
  emuDbLoaded$:Observable<boolean> = this.emuDbLoadedSubject.asObservable();
  qualityControlMethodOptions = [
    { id: "manual-review", label: "Manual review" },
    { id: "peer-review", label: "Peer review" },
    { id: "double-annotation", label: "Double annotation" },
    { id: "automated-validation", label: "Automated validation" },
    { id: "systematic-sampling", label: "Systematic sampling" },
    { id: "technical-quality-check", label: "Technical quality check" },
    { id: "perceptual-evaluation", label: "Perceptual evaluation" },
  ];

  form:FormGroup;

  formContextId:string = nanoid();
  private bodyScrollTop:number = 0;
  private previousBodyPosition:string = "";
  private previousBodyTop:string = "";
  private previousBodyWidth:string = "";
  private previousBodyOverflow:string = "";

  constructor(private http:HttpClient, private fb:FormBuilder, private systemService:SystemService, private projectService:ProjectService, private userService:UserService, private fileUploadService:FileUploadService, private notifierService: NotifierService) {
    this.emuDbIntegrationEnabled = environment.EMUDB_INTEGRATION;
    /*
    console.log(this.docsFormComponent, this.emudbFormComponent);
    setInterval(() => {
      console.log(this.docsFormComponent, this.emudbFormComponent);
    }, 1000);
    */
  }

  ngOnInit(): void {
    this.lockDocumentScroll();

    this.project = this.projectManager.projectInEdit ? this.projectManager.projectInEdit : null
    console.log(this.project);

    this.setLoadingStatus(false);

    this.docFiles = this.fb.array([]);

    this.form = this.fb.group({
      projectName: new FormControl("", {
        validators: [Validators.required, Validators.minLength(3), Validators.maxLength(30)],
        updateOn: 'blur'
      }),
      docFiles: this.docFiles,
      standardDirectoryStructure: new FormControl(true),
      createEmuDb: new FormControl(environment.EMUDB_INTEGRATION),
      description: new FormControl(""),
      financers: new FormControl(""),
      ethicsReviewDnr: new FormControl(""),
      qualityControlMethods: new FormControl([]),
      spokenLanguage: new FormControl(""),
      recordingDevice: new FormControl(""),
    });

    this.form.statusChanges.subscribe((status) => {
      this.validateForm();
    });

    this.form.controls['projectName'].setAsyncValidators([this.validateProjectName(this.http, this.userService, this.systemService)]);

    this.fileUploadService.statusStream.subscribe((status) => {
      if(status == "uploads-in-progress") {
        this.fileUploadsComlete = false;
      }
      if(status == "all-uploads-complete") {
        this.fileUploadsComlete = true;
      }
    });

    document.getElementById("projectName").focus();

    //If there's a project associated with this dialog, load it in a container so we have access to it
    if(this.project != null) {
      this.dialogTitle = this.project.name;
      
      this.form.addControl("id", new FormControl(this.project.id));
      this.form.addControl("annotationLevels", new FormControl(this.project.annotationLevels));
      this.form.addControl("annotationLinks", new FormControl(this.project.annotationLinks));
      this.form.addControl("members", new FormControl(this.project.members));
      this.form.addControl("name", new FormControl(this.project.name));
      this.form.addControl("sessions", new FormControl(this.project.sessions));

      this.form.controls.projectName.setValue(this.project.name);
      this.form.controls.projectName.disable();

      this.form.controls.description.setValue(this.project.description || "");
      this.form.controls.financers.setValue(this.project.financers || "");
      this.form.controls.ethicsReviewDnr.setValue(this.project.ethicsReviewDnr || "");
      this.form.controls.qualityControlMethods.setValue(
        this.normalizeQualityControlMethods(this.project.qualityControlMethods),
      );
      this.form.controls.spokenLanguage.setValue(this.project.spokenLanguage || "");
      this.form.controls.recordingDevice.setValue(this.project.recordingDevice || "");

      this.setLoadingStatus(true);
      console.log("Going into edit mode");
      console.log(this.project);
      console.log(this.form);
      this.setLoadingStatus(false);
    }
    else {
      this.setLoadingStatus(false);
    }

    this.validateForm();
  }

  ngOnDestroy(): void {
    this.unlockDocumentScroll();
  }

  setLoadingStatus(isLoading = true, label = "Loading") {
    this.isLoading = isLoading;
    if(isLoading) {
      //Set loading indicator
      this.submitBtnEnabled = false;
      this.showLoadingIndicator = true;
      this.submitBtnLabel = label;
      this.submitBtnEnabledLockout = true;
    }
    else {
      this.showLoadingIndicator = false;
      this.submitBtnLabel = this.project ? "Save changes" : "Create project";
      this.submitBtnEnabledLockout = false;
    }
  }
  
  validateForm() {
    if(this.fileUploadsComlete && this.form.status == "VALID" && this.isLoading == false) {
      this.submitBtnEnabled = true;
    }
    else {
      //this.submitBtnEnabled = false;
    }

    let childFormsValid = true;
    //also check if the sessions form is valid
    if (this.docsFormComponent && this.docsFormComponent.form.status != "VALID") {
      childFormsValid = false;
    }

    if (this.emudbFormComponent && !this.emudbFormComponent.formIsValid) {
      childFormsValid = false;
    }

    if(!this.fileUploadsComlete) {
      console.log("File uploads are not complete");
    }
    if(this.form.status != "VALID") {
      console.log("Form is not valid");

      Object.keys(this.form.controls).forEach((controlName) => {
        const control = this.form.controls[controlName];
        
        if (control.invalid) {
          console.log(`${controlName} is invalid`);
          console.log(control.errors); // Shows why the control is invalid
        }
      });
      
    }
    if(this.isLoading) {
      console.log("Form is loading");
    }

    /* this is a beautiful 'fix'
    if(!childFormsValid) {
      console.log("Child forms are not valid");
    }
    */
    childFormsValid = true;

    return this.fileUploadsComlete && this.form.status == "VALID" && this.isLoading == false && childFormsValid;
  }

  get projectName() {
    return this.form.get('projectName');
  }

  get standardDirectoryStructure() {
    return this.form.get('standardDirectoryStructure');
  }

  get createEmuDb() {
    return this.form.get('createEmuDb');
  }

  async saveProject(form) {
    if(!this.validateForm()) {
      this.notifierService.notify('warning', "This form is not ready to be submitted yet.");
      //this.setLoadingStatus(false);
      return false;
    }

    this.setLoadingStatus(true);

    this.projectService.loadingStatus$.subscribe(status => {
      this.submitBtnLabel = status;
    });

    let emuDbFormValues = this.emudbFormComponent.getFormGroup().getRawValue();
    if(this.docsFormComponent.status == "Uploading") {
      this.notifierService.notify('warning', "Please wait for the documentation files to finish uploading before submitting the form.");
      return false;
    }
    
    //merge with the main form
    let formValues = form.form.getRawValue();
    delete emuDbFormValues.project;

    this.emudbFormComponent.sessions.value.forEach(formSess => {
      let clearnedFiles = [];
      formSess.files.forEach(fileFormData => {
        clearnedFiles.push({
          name: fileFormData.name,
          size: fileFormData.size,
          type: fileFormData.type
        });
      });

      formSess.files = clearnedFiles;
    });

    let mergedForm = formValues;
    mergedForm.sessions = this.emudbFormComponent.sessions.value;
    mergedForm.annotLevels = emuDbFormValues.annotLevels;
    mergedForm.annotLevelLinks = emuDbFormValues.annotLevelLinks;
    mergedForm.formContextId = this.formContextId;
    mergedForm.qualityControlMethods = this.normalizeQualityControlMethods(
      mergedForm.qualityControlMethods,
    );

    mergedForm.docFiles = [];
    this.docsFormComponent.docFiles.value.forEach(docFile => {
      mergedForm.docFiles.push({
        name: docFile.name,
        size: docFile.size,
        type: docFile.type
      });
    })

    delete mergedForm.annotationLevels;
    delete mergedForm.annotationLinks;
    
    this.projectService.saveProject(mergedForm).subscribe({
      next: (data:any) => {
        this.setTaskProgressPercentage(data.progressPercentage, "submitBtn", data.msg);

        if(data.progressPercentage == 100) {
          this.projectService.fetchProjects(true).subscribe(msg => {
            this.setLoadingStatus(false);
            this.closeCreateProjectDialog();
          });
        }
      },
      error: () => {
        // The service has shown the reason; let the user fix it and save again
        this.setLoadingStatus(false);
        const submitBtn = document.getElementById("submitBtn");
        submitBtn.style.background = "";
        submitBtn.style.color = "";
        this.validateForm();
      },
    });
  }

  setTaskProgressPercentage(progressPercent, targetElementId = null, msg = null) {
    if(targetElementId != null) {
      let targetEl = document.getElementById(targetElementId);
      targetEl.style.background = 'linear-gradient(90deg, #73A790 '+progressPercent+'%, #654c4f '+progressPercent+'%)';
      targetEl.style.color = "#fff";
    }
    if(msg != null) {
      this.submitBtnLabel = msg;
    }
  }

  setTaskProgress(progressStep, totalNumSteps, targetElementId = null, msg = null) {
    console.log("setTaskProgress", progressStep, totalNumSteps, targetElementId, msg);
    let progressPercent = Math.ceil((progressStep / totalNumSteps) * 100);
    if(targetElementId != null) {
      let targetEl = document.getElementById(targetElementId);
      targetEl.style.background = 'linear-gradient(90deg, #73A790 '+progressPercent+'%, #654c4f '+progressPercent+'%)';
      targetEl.style.color = "#fff";
    }
    if(msg != null) {
      this.submitBtnLabel = msg;
    }
  }

  /**
   * Whether closing now would throw away anything the user has done. Uploaded
   * files, new sessions and deleted sessions don't mark the forms dirty, so
   * they are checked separately.
   */
  get hasUnsavedChanges():boolean {
    if(this.form.dirty || this.emudbFormComponent?.form?.dirty) {
      return true;
    }
    if(this.docsFormComponent?.docFiles?.value?.length > 0) {
      return true;
    }
    const sessions = this.emudbFormComponent?.sessionForms?.controls || [];
    return sessions.some(session => {
      if(session.get('deleted').value) {
        return true;
      }
      // Files dropped in this dialog are only added to the session on save
      if(session.get('files').value.some(file => !file.stored)) {
        return true;
      }
      if(session.get('new').value) {
        //a new project always starts out with one empty session
        return this.project != null || session.get('files').value.length > 0;
      }
      return false;
    });
  }

  cancel() {
    if(this.isLoading) {
      this.notifierService.notify('warning', "Please wait for the current task to finish before closing the dialog.");
      return;
    }
    const question = this.project ? "Discard your unsaved changes to this project?" : "Discard this new project?";
    if(this.hasUnsavedChanges && !window.confirm(question)) {
      return;
    }
    this.closeCreateProjectDialog();
  }

  closeCreateProjectDialog() {
    if(this.isLoading) {
      this.notifierService.notify('warning', "Please wait for the current task to finish before closing the dialog.");
      return;
    }
    this.fileUploadService.reset();
    this.projectManager.dashboard.modalActive = false;
  }

  private lockDocumentScroll() {
    this.bodyScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    this.previousBodyPosition = document.body.style.position;
    this.previousBodyTop = document.body.style.top;
    this.previousBodyWidth = document.body.style.width;
    this.previousBodyOverflow = document.body.style.overflow;

    document.body.style.position = "fixed";
    document.body.style.top = `-${this.bodyScrollTop}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
  }

  private unlockDocumentScroll() {
    document.body.style.position = this.previousBodyPosition;
    document.body.style.top = this.previousBodyTop;
    document.body.style.width = this.previousBodyWidth;
    document.body.style.overflow = this.previousBodyOverflow;

    window.scrollTo(0, this.bodyScrollTop);
  }

  onAudioUpload(event, session) {
    let allowedFilesTypes = ['audio/wav', 'audio/x-wav'];

    for(let key in event.addedFiles) {
      if(allowedFilesTypes.includes(event.addedFiles[key].type) == false) {
        this.notifierService.notify('warning', 'There file ' + event.addedFiles[key].name + ' is of an invalid type ' + event.addedFiles[key].type + '. Please only upload WAV files here.');
        event.addedFiles.splice(key, 1);
      }
    }

    if(event.addedFiles.length == 0) {
      return;
    }

    session.value.files.push(...event.addedFiles);

    for(let key in event.addedFiles) {
      let file = event.addedFiles[key];
      this.uploadFile(file, session).then(() => {
      })
    }
  }

  async uploadFile(file:File, session) {
    return await this.fileUploadService.upload(file, this.formContextId, "emudb-sessions/"+session.controls.id.value);
  }
  
  onRemove(event, session) {
    session.value.files.splice(session.value.files.indexOf(event), 1);
    //TODO: Remove the uploaded file from the server?
  }

  validateProjectName(http:HttpClient, userService:UserService, systemService:SystemService) {
    return function(control:AbstractControl):Observable<ValidationErrors> | null {
      const value: string = control.value;
      let session = userService.getSession();

      //use websocket backend
      let requestId = nanoid();
      //create a websocket request


      return new Observable((observer) => {
        systemService.wsSubject.subscribe((data:any) => {
          if(data.type == "cmd-result" && data.cmd == "validateProjectName") {
            if(data.requestId == requestId && data.progress == "end") {
              let result = null;
              if(data.result == false) {
                //control.setErrors({ isFormNameTaken: true });
                if(data.message == "Project name already exists") {
                  result = { 'isFormNameTaken': true };
                }
                if(data.message == "Project name must be alphanumeric") {
                  result = { 'pattern': true };
                }
              }
              observer.next(result);
              observer.complete();
            }
          }
        });
  
        systemService.ws.send(JSON.stringify({
          requestId: requestId,
          type: 'cmd',
          cmd: 'validateProjectName',
          projectName: value,
        }));

      });
    }
  }

  onDocDrop(event) {
    this.docFiles.value.push(...event.addedFiles);
  }

  private normalizeQualityControlLookupToken(value:any) {
    return String(value || "")
      .toLowerCase()
      .replace(/[`*_]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  private resolveQualityControlMethodId(value:any):string | null {
    const lookupValue = this.normalizeQualityControlLookupToken(value);
    if(!lookupValue) {
      return null;
    }

    for(const option of this.qualityControlMethodOptions) {
      if(
        lookupValue == this.normalizeQualityControlLookupToken(option.id) ||
        lookupValue == this.normalizeQualityControlLookupToken(option.label)
      ) {
        return option.id;
      }
    }
    return null;
  }

  normalizeQualityControlMethods(value:any):string[] {
    let rawValues:any[] = [];
    if(Array.isArray(value)) {
      rawValues = value;
    }
    else if(typeof value == "string" && value.trim().length > 0) {
      rawValues = value.split(/[;,]/);
    }

    let normalized:string[] = [];
    rawValues.forEach((entry) => {
      const methodId = this.resolveQualityControlMethodId(entry);
      if(methodId && normalized.includes(methodId) == false) {
        normalized.push(methodId);
      }
    });
    return normalized;
  }
  

}
